import { useState, useEffect, useCallback } from "react";
import { Wallet, ShieldCheck, RefreshCw } from "lucide-react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { financeBackfill, financeSummary, financeDaily, financeUsage, financeFeatures, financeEntries, financeSettings } from "@/lib/financeApi";
import { CreditSummary } from "../components/finance/CreditSummary";
import { DailyTable } from "../components/finance/DailyTable";
import { LedgerTable } from "../components/finance/LedgerTable";
import { FinanceSettings } from "../components/finance/FinanceSettings";

const iso = (d) => d.toISOString().slice(0, 10);
const RANGES = {
  today: () => { const t = new Date(); return [iso(t), iso(t)]; },
  yesterday: () => { const t = new Date(); t.setDate(t.getDate() - 1); return [iso(t), iso(t)]; },
  "7d": () => { const t = new Date(), s = new Date(); s.setDate(t.getDate() - 6); return [iso(s), iso(t)]; },
  "30d": () => { const t = new Date(), s = new Date(); s.setDate(t.getDate() - 29); return [iso(s), iso(t)]; },
  month: () => { const t = new Date(); return [iso(new Date(t.getFullYear(), t.getMonth(), 1)).slice(0, 10), iso(t)]; },
};
const VIEWS = [["all", "Everything"], ["operations", "Daily operations"], ["development", "Development"], ["purchase", "Purchases"], ["other", "Other"], ["balance_checkin", "Balance check-ins"]];

export default function Finance() {
  const { user } = useAuth();
  const authed = !!user?.permissions?.manage;
  const [range, setRange] = useState("30d");
  const [custom, setCustom] = useState(["", ""]);
  const [feature, setFeature] = useState("");
  const [view, setView] = useState("all");
  const [features, setFeatures] = useState([]);
  const [summary, setSummary] = useState(null);
  const [daily, setDaily] = useState(null);
  const [usage, setUsage] = useState([]);
  const [entries, setEntries] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);

  const [start, end] = range === "custom" ? custom : RANGES[range]();

  const load = useCallback(async () => {
    if (!authed || !start || !end) return;
    setLoading(true);
    try {
      const [s, d, u, e, f, st] = await Promise.all([financeSummary(), financeDaily(start, end, feature || undefined), financeUsage(start, end, feature || undefined), financeEntries(undefined, start, end), financeFeatures(), financeSettings()]);
      setSummary(s); setDaily(d); setUsage(u.rows); setEntries(e.entries); setFeatures(f.features); setSettings(st);
    } catch (e) {
      toast.error("Failed to load finance data");
    } finally { setLoading(false); }
  }, [authed, start, end, feature]);
  useEffect(() => { load(); }, [load]);

  const onEntryChanged = async () => { const [s, e] = await Promise.all([financeSummary(), financeEntries(undefined, start, end)]); setSummary(s); setEntries(e.entries); };

  if (!authed) return <Navigate to="/" replace />;

  const byCat = (c) => entries.filter((e) => e.category === c);
  const showOps = view === "all" || view === "operations";
  const ledgers = view === "all" ? ["development", "purchase", "other", "balance_checkin"] : VIEWS.map(([k]) => k).filter((k) => k === view && k !== "operations");

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Wallet className="h-6 w-6 text-primary" /> Finance &amp; credit consumption</h1>
          <p className="text-sm text-muted-foreground mt-1">Purchased vs utilised credits — daily operations separated from new development. Admin only.</p>
        </div>
        <div className="flex items-center gap-2">
        <button data-testid="finance-recalibrate" title="Rebuild the pre-meter history using the average size of metered calls (still labelled Estimated)" onClick={async () => { try { const r = await financeBackfill(true); if (r.skipped) toast.info(`Nothing to rebuild — ${r.reason}`); else toast.success(`Estimated history rebuilt: ${r.created} rows before ${r.cutoff}`); load(); } catch { toast.error("Rebuild failed"); } }} className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs hover:bg-secondary"><RefreshCw className="h-3.5 w-3.5" /> Recalibrate estimated history</button>
        <span data-testid="finance-admin-badge" className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs text-muted-foreground"><ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Admin session · {user.email}</span>
        </div>
      </div>

      <CreditSummary s={summary} />

      {/* filters */}
      <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="finance-filters">
        <div className="flex items-center rounded-lg border p-0.5 font-medium">
          {[["today", "Today"], ["yesterday", "Yesterday"], ["7d", "Last 7 days"], ["30d", "Last 30 days"], ["month", "This month"], ["custom", "Custom"]].map(([k, l]) => (
            <button key={k} data-testid={`fin-range-${k}`} onClick={() => setRange(k)} className={`rounded-md px-2.5 py-1 transition-colors ${range === k ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>
          ))}
        </div>
        {range === "custom" && (
          <div className="flex items-center gap-1">
            <input data-testid="fin-custom-start" type="date" value={custom[0]} onChange={(e) => setCustom([e.target.value, custom[1]])} className="rounded-md border bg-background px-2 py-1" />
            <span>→</span>
            <input data-testid="fin-custom-end" type="date" value={custom[1]} onChange={(e) => setCustom([custom[0], e.target.value])} className="rounded-md border bg-background px-2 py-1" />
          </div>
        )}
        <select data-testid="fin-view" value={view} onChange={(e) => setView(e.target.value)} className="rounded-md border bg-background px-2 py-1.5">
          {VIEWS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select data-testid="fin-feature" value={feature} onChange={(e) => setFeature(e.target.value)} className="rounded-md border bg-background px-2 py-1.5">
          <option value="">All features / services</option>
          {features.map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        <span className="text-muted-foreground">{start} → {end}{loading ? " · loading…" : ""}</span>
      </div>

      {showOps && <DailyTable data={daily} usage={usage} unit={summary?.credit_unit || "credits"} />}
      {ledgers.map((c) => <LedgerTable key={c} category={c} entries={byCat(c)} onChanged={onEntryChanged} unit={summary?.credit_unit || "credits"} />)}
      <FinanceSettings s={settings} onSaved={load} />
    </div>
  );
}
